import { useMemo } from 'react';
import { useIntl } from 'react-intl';

import usePageProps from '@/hooks/usePageProps';
import {
  useGetCitiesListQuery,
  useGetOrganizationBySlugQuery,
  useGetOrganizationsListQuery,
} from '@/queries';

const getLabeledOptions = ({ values, formatMessage, key }) =>
  values.map((value) => ({
    value,
    label: formatMessage({
      id: `productFiltersForm.${key}.label${value ? `.${value}` : ''}`,
    }),
  }));

export function useLabeledOptions(values, key) {
  const { formatMessage } = useIntl();

  return useMemo(
    () => getLabeledOptions({ values, formatMessage, key }),
    [formatMessage, key, values]
  );
}

const PROVISION_FORM_ELEMENTS = [
  'one_time',
  'overdraft',
  'credit_line',
  'leasing',
  'revolving_credit_line',
  'non_revolving_credit_line',
];

const SUPPORT_PROGRAM_ELEMENTS = [
  'government',
  'international',
  'partner',
  'grant',
  'custom_program',
];

const IS_LOAN_SECURITY_ELEMENTS = ['0', '1'];

const LOAN_SECURITY_ELEMENTS = [
  'no_collateral',
  'real_estate',
  'auto',
  'deposit',
  'ovdp',
  'property',
  'purchased_with_loan',
  'equipment',
  'securities',
  'pledge',
  'other',
];

const LOAN_RECIPIENT_ELEMENTS = ['fop', 'legal_entity', 'osbb', 'farmer'];

const ACTIVE_SCOPE_ELEMENTS = [
  'it',
  'agro',
  'construction',
  'trading',
  'manufacturing',
  'logistics',
  'education',
  'healthcare',
  'hospitality',
  'finance',
  'real_estate',
  'energy',
  'transport',
  'marketing',
  'consulting',
  'food_industry',
  'export_activity',
];

const BUSINESS_SIZE_ELEMENTS = ['msb', 'mb', 'corporate'];

export function useHiddenFilters() {
  const { intl, params } = usePageProps();
  const { formatMessage, locale } = intl;
  const { data: companyData } = useGetOrganizationBySlugQuery(
    {
      locale,
      slug: params?.companySlug || '',
    },
    { skip: !params?.companySlug }
  );
  const { data: companies } = useGetOrganizationsListQuery({ locale });
  const { data: cities } = useGetCitiesListQuery({ locale });

  // Прибираємо організацію в списку зі сторінки РВК
  const companiesWitOutCurrent =
    companyData?.id !== undefined
      ? (({ [companyData.id]: _, ...rest }) => rest)(companies)
      : companies;

  return useMemo(
    () => ({
      checkboxList: [
        {
          id: 'online_submission',
          label: formatMessage({
            id: 'productFiltersForm.online_submission.label',
          }),
        },
        {
          id: 'is_prepayment',
          label: formatMessage({
            id: 'productFiltersForm.is_prepayment.label',
          }),
        },
        {
          id: 'personal_assistance',
          label: formatMessage({
            id: 'productFiltersForm.personal_assistance.label',
          }),
        },
        {
          id: 'online_cabinet',
          label: formatMessage({
            id: 'productFiltersForm.online_cabinet.label',
          }),
        },
        {
          id: 'is_loan_surety',
          label: formatMessage({
            id: 'productFiltersForm.is_loan_surety.label',
          }),
        },
      ],
      hiddenElements: [
        {
          id: 'location_id',
          type: 'miltySelect',
          options: cities?.map((item) => ({
            value: item?.id || '',
            label: item?.name || '',
          })),
        },
        {
          id: 'vendor_id',
          type: 'miltySelect',
          options: Object.values(companiesWitOutCurrent || {}).map((item) => ({
            value: item?.id || '',
            label: item?.companyName || '',
          })),
        },
        {
          id: 'provision_form',
          type: 'select',
          options: getLabeledOptions({
            formatMessage,
            key: 'provision_form',
            values: PROVISION_FORM_ELEMENTS,
          }),
        },
        {
          id: 'support_program',
          type: 'select',
          options: getLabeledOptions({
            formatMessage,
            key: 'support_program',
            values: SUPPORT_PROGRAM_ELEMENTS,
          }),
        },
        {
          id: 'is_loan_security',
          type: 'select',
          options: getLabeledOptions({
            formatMessage,
            key: 'is_loan_security',
            values: IS_LOAN_SECURITY_ELEMENTS,
          }),
        },
        {
          id: 'loan_security',
          type: 'select',
          options: getLabeledOptions({
            formatMessage,
            key: 'loan_security',
            values: LOAN_SECURITY_ELEMENTS,
          }),
        },
        {
          id: 'loan_recipient',
          type: 'miltySelect',
          options: getLabeledOptions({
            formatMessage,
            key: 'loan_recipient',
            values: LOAN_RECIPIENT_ELEMENTS,
          }),
        },
        {
          id: 'business_size',
          type: 'miltySelect',
          options: getLabeledOptions({
            formatMessage,
            key: 'business_size',
            values: BUSINESS_SIZE_ELEMENTS,
          }),
        },
        {
          id: 'activity_scope',
          type: 'miltySelect',
          options: getLabeledOptions({
            formatMessage,
            key: 'activity_scope',
            values: ACTIVE_SCOPE_ELEMENTS,
          }),
        },
        {
          id: 'registration_period',
          type: 'input',
        },
      ],
    }),
    [formatMessage, companies]
  );
}
